import type { AgentCursor } from "@/stores/canvas-store";
import {
  DESKTOP_APPROVAL,
  type DesktopApprovalPoint,
} from "@/lib/desktop-approval-question";

/** Whether the cursor holds the computer-use approval gate's question, which
 *  it asks itself, at its target, even beside a frame that shows its run. */
export function asksDesktopApproval(cursor: AgentCursor): boolean {
  return cursor.question?.kind === DESKTOP_APPROVAL;
}

/** Where the cursor asks its desktop approval: the approval's target point,
 *  else where the agent last acted. Null for any other cursor. */
export function desktopApprovalPoint(
  cursor: AgentCursor,
): DesktopApprovalPoint | null {
  return asksDesktopApproval(cursor)
    ? (cursor.question?.desktopTarget ?? null)
    : null;
}

/**
 * Cursor states that need a physical screen location: a point, a live
 * drawing, or a live run operating the user's real desktop. They stay
 * visible while the compact dock owns ordinary status and while a
 * conversation frame owns the turn. Desktop ownership outlives the run, so it
 * stops counting once the run is frozen, failed, or awaiting input. A pending
 * question is not attention intent; it shows on its run's one surface: the
 * cursor's own pill when that cursor owns the run, else its work toast or
 * frame, with a "?" badge on a visible cursor. A desktop approval is the
 * exception: the cursor asks it at its target.
 */
export function hasAgentCursorAttentionIntent(cursor: AgentCursor): boolean {
  return !!(
    cursor.pointTarget ||
    asksDesktopApproval(cursor) ||
    (cursor.annotationTarget && !cursor.annotationTarget.dismissed) ||
    (cursor.executionId &&
      cursor.desktopInteractionExecutionId === cursor.executionId &&
      !cursor.frozen &&
      !cursor.error &&
      !cursor.question)
  );
}

/**
 * A cursor that stays beside the visible conversation frame showing its run
 * keeps its arrow and drops the status that frame already shows. An explicit
 * point presentation keeps its narration, and a desktop approval its question.
 */
export function shouldSuppressFrameOwnedCursorStatus(
  cursor: AgentCursor,
  frameOwned: boolean,
): boolean {
  return (
    frameOwned &&
    cursor.presentation !== "point" &&
    !cursor.presentationDeliveryId &&
    !asksDesktopApproval(cursor)
  );
}
